import { useEffect, useState } from "react";
import {
  apiFetch,
  DEV_PROFILE_STORAGE_KEY,
  devProfileOverride,
} from "@shared/api-client";
import styles from "./DevProfileSwitcher.module.css";

/**
 * Dev-only profile switcher for the inquiry form. Renders ONLY on a bare
 * localhost host. It flips which discipline profile of the SAME tenant (the
 * demo Company) the form loads — the picked route_slug is stored in
 * localStorage and read by useInquiryProfile / devProfileOverride, which loads
 * `/api/inquiry/profiles/{slug}` instead of the tenant default. A reload keeps
 * it simple: drafts, the parser and the estimate all key on the profile, so a
 * clean mount is the correct way to swap disciplines.
 *
 * The options come from the same public profile endpoints the flow itself
 * uses — no second hardcoded profile registry (the shape ADR-0008 deleted).
 * There is no public list endpoint, so known demo slugs are probed against
 * `/api/inquiry/profiles/{slug}`: only profiles the tenant actually serves
 * are offered, labelled by the server's authoritative `label`, and the
 * tenant default (shown when no override is stored) comes from
 * `/api/inquiry/profile`.
 */
const CANDIDATE_SLUGS = ["live-sound", "staging", "lighting", "power-hire"];

interface DevProfileOption {
  slug: string;
  label: string;
}

/** Minimal read of the public profile payload — identity only. */
async function fetchProfileOption(
  path: string,
): Promise<DevProfileOption | null> {
  try {
    const payload = await apiFetch<unknown>(path);
    if (typeof payload !== "object" || payload === null) return null;
    const { label, routeSlug } = payload as Record<string, unknown>;
    if (typeof label !== "string" || typeof routeSlug !== "string") return null;
    return { slug: routeSlug, label };
  } catch {
    return null; // 404 (profile not served here) or network — just omit it.
  }
}

export function DevProfileSwitcher(): React.JSX.Element | null {
  const isLocalhost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  const [defaultSlug, setDefaultSlug] = useState<string | null>(null);
  const [options, setOptions] = useState<DevProfileOption[]>([]);

  useEffect(() => {
    if (!isLocalhost) return;
    let active = true;
    async function load(): Promise<void> {
      const fallback = await fetchProfileOption("/api/inquiry/profile");
      if (!fallback) return; // no tenant default — nothing to switch
      // A hand-stored override rides along so it stays visible in the list.
      const stored = devProfileOverride();
      const candidates = [
        ...new Set([...CANDIDATE_SLUGS, ...(stored ? [stored] : [])]),
      ].filter((slug) => slug !== fallback.slug);
      const probed = await Promise.all(
        candidates.map((slug) =>
          fetchProfileOption(`/api/inquiry/profiles/${slug}`),
        ),
      );
      if (!active) return;
      setDefaultSlug(fallback.slug);
      setOptions([
        fallback,
        ...probed.filter(
          (option): option is DevProfileOption => option !== null,
        ),
      ]);
    }
    void load();
    return () => {
      active = false;
    };
  }, [isLocalhost]);

  if (!isLocalhost || defaultSlug === null || options.length < 2) return null;

  let current = defaultSlug;
  try {
    current = localStorage.getItem(DEV_PROFILE_STORAGE_KEY) ?? defaultSlug;
  } catch {
    /* storage unavailable */
  }

  const handleChange = (slug: string): void => {
    try {
      if (slug === defaultSlug) {
        localStorage.removeItem(DEV_PROFILE_STORAGE_KEY);
      } else {
        localStorage.setItem(DEV_PROFILE_STORAGE_KEY, slug);
      }
    } catch {
      /* storage unavailable */
    }
    window.location.reload();
  };

  return (
    <div className={styles.switcher}>
      <span className={styles.label}>Profile (dev)</span>
      <select
        aria-label="Dev profile switcher"
        value={options.some((p) => p.slug === current) ? current : defaultSlug}
        onChange={(e) => handleChange(e.target.value)}
        className={styles.select}
      >
        {options.map((p) => (
          <option key={p.slug} value={p.slug}>
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );
}
